"use client";

import { useCallback } from "react";
import { checkoutMarketingContext } from "@/lib/tracking/marketing-events";
import { paymentService } from "@/lib/api/services";
import { buildMoyasarConfig, type MoyasarInitResponse } from "@/lib/utils/moyasar";
import type { CartItem, ProcessCartItem, ShippingAddress } from "@/types";
import { EnumPaymentMethods, EnumPaymentGateway } from "@/types";
import type { UnifiedDonationProjectParams } from "./unifiedDonationTypes";
import { buildPayOneProjectRequest } from "./buildPayOneProjectRequest";
import { mapCartItemsToProcessCartItems } from "./cartProcessCartMapper";

export interface PreparePaymentResult {
  opCode: string;
  payAmount: number;
  moyasarConfigData: MoyasarInitResponse;
}
export interface UsePrepareDonationPaymentParams {
  projectParams: UnifiedDonationProjectParams | null | undefined;
  cartItems: CartItem[];
  associationId: number;
  total: number;
}

function mapDeductionParamsToProcessCartItem(
  params: UnifiedDonationProjectParams,
): ProcessCartItem {
  return {
    projectId: params.projectId,
    amount: params.amount,
    quantity: params.quantity || 1,
    shareId: params.shareId,
    categoryId: params.categoryId,
    donateTypeId: params.donateTypeId,
    isRecurring: true,
    recurringDate: params.recurringDate,
    periodType: params.periodType,
    dayNumber: params.dayNumber,
    totalPeriod: params.totalPeriod,
    openEnded: params.openEnded,
  };
}

function createAuthRequiredError(message?: string): Error & { status: number } {
  const error = new Error(message || "Authentication required") as Error & {
    status: number;
  };
  error.name = "AuthRequiredError";
  error.status = 401;
  return error;
}

/**
 * Centralizes "create donation + get Moyasar config" for single-project and cart.
 * Used by UnifiedDonationModal for fast donation, auth success, and payment-step effect.
 */
export function usePrepareDonationPayment({
  projectParams,
  cartItems,
  associationId,
  total,
}: UsePrepareDonationPaymentParams) {
  const createFromProcessCart = useCallback(
    async (
      body: Record<string, unknown>,
      donorMobile: string,
    ): Promise<PreparePaymentResult> => {
      const response = await fetch("/api/payments/process-cart", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          marketingContext: checkoutMarketingContext(),
          paymentMethod: "card",
          paymentMethodId: EnumPaymentMethods.Visa,
          paymentGateway: EnumPaymentGateway.Moyasar,
          associationId: associationId || 0,
          ...body,
        }),
      });
      const payload = (await response.json().catch(() => null)) as
        | {
            success?: boolean;
            message?: string;
            data?: { operationCode?: string; totalAmount?: number; amount?: number };
          }
        | null;

      if (response.status === 401) {
        throw createAuthRequiredError(payload?.message);
      }

      if (!payload?.success || !payload.data?.operationCode) {
        throw new Error(payload?.message || "Failed to create donation");
      }
      const opCode = payload.data.operationCode;
      const payAmount = payload.data.amount ?? payload.data.totalAmount ?? total;

      const moyasarConfigData = await buildMoyasarConfig({
        opCode,
        payAmount,
        associationId: associationId || "",
        mobile: donorMobile || "",
      });

      return { opCode, payAmount, moyasarConfigData };
    },
    [associationId, total],
  );

  const prepareCart = useCallback(
    async (
      donorMobile: string,
      donorName: string,
      shippingAddress?: ShippingAddress,
    ): Promise<PreparePaymentResult> => {
      const items: ProcessCartItem[] = mapCartItemsToProcessCartItems(cartItems);

      return createFromProcessCart(
        {
          items,
          donorMobile: donorMobile.trim() || undefined,
          donorName: donorName.trim() || undefined,
          shippingAddress,
        },
        donorMobile,
      );
    },
    [cartItems, createFromProcessCart],
  );

  /**
   * A deduction is paid on its own: the schedule travels in the body and the
   * backend creates the agreement as part of this payment, so the donor's cart
   * is neither read nor charged.
   */
  const prepareStandaloneDeduction = useCallback(
    async (
      donorMobile: string,
      donorName?: string,
    ): Promise<PreparePaymentResult> => {
      if (!projectParams) {
        throw new Error("No deduction params");
      }
      return createFromProcessCart(
        {
          checkoutSource: "standalone",
          items: [mapDeductionParamsToProcessCartItem(projectParams)],
          donorMobile: donorMobile.trim() || undefined,
          donorName: donorName?.trim() || undefined,
        },
        donorMobile,
      );
    },
    [projectParams, createFromProcessCart],
  );

  const prepareSingleProject = useCallback(
    async (mobile: string): Promise<PreparePaymentResult> => {
      if (!projectParams) {
        throw new Error("No project params");
      }
      if (projectParams.isRecurring) {
        return prepareStandaloneDeduction(mobile);
      }
      const response = await paymentService.payOneProject(
        buildPayOneProjectRequest(projectParams, mobile, undefined),
      );

      if (!response.success || !response.data?.operationCode) {
        throw new Error(response.message || "Failed to create donation");
      }
      const opCode = response.data.operationCode;
      const payAmount =
        response.data.totalAmount ?? response.data.amount ?? total;

      const moyasarConfigData = await buildMoyasarConfig({
        opCode,
        payAmount,
        associationId: projectParams.associationId || associationId || "",
        mobile,
      });

      return { opCode, payAmount, moyasarConfigData };
    },
    [projectParams, associationId, total, prepareStandaloneDeduction],
  );

  /** For payment-step effect: prepare single project when already on payment view (e.g. logged-in user). */
  const prepareSingleProjectWithDonor = useCallback(
    async (
      donorMobile: string,
      donorName: string,
    ): Promise<PreparePaymentResult> => {
      if (!projectParams) {
        throw new Error("No payment parameters provided");
      }
      if (projectParams.isRecurring) {
        return prepareStandaloneDeduction(donorMobile, donorName);
      }
      const response = await paymentService.payOneProject(
        buildPayOneProjectRequest(projectParams, donorMobile, donorName),
      );

      if (!response.success || !response.data?.operationCode) {
        throw new Error(response.message || "Failed to create donation");
      }
      const opCode = response.data.operationCode;
      const payAmount =
        response.data.totalAmount ?? response.data.amount ?? total;

      const moyasarConfigData = await buildMoyasarConfig({
        opCode,
        payAmount,
        associationId: projectParams.associationId || associationId || "",
        mobile: donorMobile || "",
      });

      return { opCode, payAmount, moyasarConfigData };
    },
    [projectParams, associationId, total, prepareStandaloneDeduction],
  );

  return {
    prepareCart,
    prepareSingleProject,
    prepareSingleProjectWithDonor,
  };
}
